"use client";
import type React from "react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Building2,
  Shield,
  Smartphone,
  Mail,
  Hash,
  Key,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import VerificationModal from "../components/VerificationModal";

interface LoginForm {
  email: string;
  password: string;
  mobile: string;
  gstNo: string;
  tally_serialno: string;
  rememberMe: boolean;
}

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [loginError, setLoginError] = useState(""); // This state is not currently used in the UI, but kept for potential future use.
  const { login, isLoading } = useAuthStore();
  const {
    register,
    handleSubmit,
    setValue, // Added setValue to programmatically set form values
    formState: { errors },
  } = useForm<LoginForm>();

  useEffect(() => {
    // Load saved credentials from localStorage on component mount
    const savedCredentials = localStorage.getItem("rememberedLogin");
    if (savedCredentials) {
      try {
        const { email, mobile, gstNo, tally_serialno, rememberMe } =
          JSON.parse(savedCredentials);
        setValue("email", email);
        setValue("mobile", mobile);
        setValue("gstNo", gstNo);
        setValue("tally_serialno", tally_serialno);
        setValue("rememberMe", rememberMe);
      } catch (e) {
        console.error("Failed to parse saved credentials from localStorage", e);
        localStorage.removeItem("rememberedLogin"); // Clear invalid data
      }
    }
  }, [setValue]);

  const onSubmit = async (data: LoginForm) => {
    setLoginError(""); // Reset error first
    const result = await login(data);

    if (result.success) {
      if (data.rememberMe) {
        // Save credentials if "Remember me" is checked
        localStorage.setItem(
          "rememberedLogin",
          JSON.stringify({
            email: data.email,
            mobile: data.mobile,
            gstNo: data.gstNo,
            tally_serialno: data.tally_serialno,
            rememberMe: true,
          })
        );
      } else {
        // Clear saved credentials if "Remember me" is unchecked
        localStorage.removeItem("rememberedLogin");
      }

      if (result.requiresVerification) {
        setShowVerification(true);
      }
    } else {
      // Handle login failure, if any specific error message is returned
      // setLoginError(result.message || "Login failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Enhanced Illustration */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
        className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden"
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          {/* Geometric Shapes */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
          <div className="absolute top-40 right-32 w-24 h-24 bg-white/10 rounded-full blur-lg"></div>
          <div className="absolute bottom-32 left-16 w-40 h-40 bg-white/5 rounded-full blur-2xl"></div>
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-5">
            <svg
              className="w-full h-full"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern
                  id="grid"
                  width="10"
                  height="10"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 10 0 L 0 0 0 10"
                    fill="none"
                    stroke="white"
                    strokeWidth="0.5"
                  />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#grid)" />
            </svg>
          </div>
        </div>
        {/* Main Content Container */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full px-16 py-12">
          {/* Header Section */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-full mx-auto mb-6 bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-2xl">
              {/* <img
                src="https://canara.connectedbanking.co.in/public/images/canaralogo.png"
                alt="Canara Bank Logo"
                className="w-full object-contain"
              /> */}
            </div>
            <h1 className="text-4xl font-bold text-white mb-3">Canara Bank</h1>
            <p className="text-lg text-blue-100 font-medium">
              Professional Banking Solutions
            </p>
          </motion.div>
        </div>
      </motion.div>
      {/* Right Side - Login Form */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50"
      >
        <div className="w-full max-w-md">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-center mb-8"
          >
            <div className="lg:hidden w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome Back
            </h2>
            <p className="text-gray-600">Sign in to your SecureBank account</p>
          </motion.div>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* First Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail size={16} className="inline mr-2" />
                    Email Address
                  </label>
                  <input
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email address",
                      },
                    })}
                    type="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Smartphone size={16} className="inline mr-2" />
                    Mobile Number
                  </label>
                  <input
                    {...register("mobile", {
                      required: "Mobile number is required",
                    })}
                    type="tel"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter mobile number"
                  />
                  {errors.mobile && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.mobile.message}
                    </p>
                  )}
                </div>
              </div>
              {/* Second Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Hash size={16} className="inline mr-2" />
                    GST Number
                  </label>
                  <input
                    {...register("gstNo", {
                      required: "GST number is required",
                    })}
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter GST number"
                  />
                  {errors.gstNo && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.gstNo.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Key size={16} className="inline mr-2" />
                    Tally Serial Number
                  </label>
                  <input
                    {...register("tally_serialno", {
                      required: "Tally serial number is required",
                    })}
                    type="number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter Tally serial number"
                  />
                  {errors.tally_serialno && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.tally_serialno.message}
                    </p>
                  )}
                </div>
              </div>
              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Shield size={16} className="inline mr-2" />
                  Password
                </label>
                <div className="relative">
                  <input
                    {...register("password", {
                      required: "Password is required",
                    })}
                    type={showPassword ? "text" : "password"}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>
              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    {...register("rememberMe")}
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Remember me
                  </span>
                </label>
                <Link
                  to="/reset-password"
                  className="text-sm text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Secure Login
                  </>
                )}
              </button>
            </form>
          </motion.div>
          {/* Security Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-6 text-center"
          >
            <p className="text-xs text-gray-500">
              🔒 Your connection is secured with 256-bit SSL encryption
            </p>
          </motion.div>
        </div>
      </motion.div>
      {showVerification && (
        <VerificationModal onClose={() => setShowVerification(false)} />
      )}
    </div>
  );
};

export default LoginPage;
